import React, { useState, useMemo, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import TabNavigation from '../../components/ui/TabNavigation';
import PersonColorLegend from '../../components/ui/PersonColorLegend';
import QuickActionBar from '../../components/ui/QuickActionBar';
import CalendarHeader from './components/CalendarHeader';
import CalendarGrid from './components/CalendarGrid';
import WeekView from './components/WeekView';
import DayView from './components/DayView';
import ActivityModal from './components/ActivityModal';
import SearchFilter from './components/SearchFilter';
import ExportImportControls from './components/ExportImportControls';
import ReminderIndicator from './components/ReminderIndicator';
import GoogleCalendarSyncButton from './components/GoogleCalendarSyncButton';
import { generateRecurringInstances, hasRecurringSchedule } from '../../utils/recurringUtils';
import { getUpcomingReminders, shouldShowReminder } from '../../utils/notificationUtils';


const FamilyCalendarView = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('month');
  const [isActivityModalOpen, setIsActivityModalOpen] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [draggedActivity, setDraggedActivity] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPersonFilters, setSelectedPersonFilters] = useState([]);
  const [activeReminders, setActiveReminders] = useState([]);
  const [dismissedReminders, setDismissedReminders] = useState([]);

  // Load preferences from localStorage or use defaults
  const [preferences, setPreferences] = useState(() => {
    const saved = localStorage.getItem('familyPreferences');
    return saved ? JSON.parse(saved) : {
      googleCalendarConnected: false,
      googleCalendarId: null,
      googleCalendarAutoImport: true,
      googleCalendarTwoWaySync: false,
      holidayLocation: 'Australia',
      showPublicHolidays: true,
      showSchoolTerms: true,
    };
  });

  // Mock notification settings (in real app, would come from settings)
  const notificationSettings = [
    { memberId: 1, enabled: true, frequency: ['15min', '1day'], deliveryTiming: 'smart', quietHoursStart: '22:00', quietHoursEnd: '07:00', notifyForRecurring: true },
    { memberId: 2, enabled: true, frequency: ['30min', '1day'], deliveryTiming: 'smart', quietHoursStart: '22:00', quietHoursEnd: '07:00', notifyForRecurring: true },
    { memberId: 3, enabled: true, frequency: ['15min'], deliveryTiming: 'smart', quietHoursStart: '22:00', quietHoursEnd: '07:00', notifyForRecurring: true },
    { memberId: 4, enabled: true, frequency: ['15min', '1hour'], deliveryTiming: 'smart', quietHoursStart: '22:00', quietHoursEnd: '07:00', notifyForRecurring: true },
    { memberId: 5, enabled: true, frequency: ['30min'], deliveryTiming: 'smart', quietHoursStart: '22:00', quietHoursEnd: '07:00', notifyForRecurring: true },
    { memberId: 6, enabled: true, frequency: ['15min', '1day'], deliveryTiming: 'smart', quietHoursStart: '22:00', quietHoursEnd: '07:00', notifyForRecurring: true }
  ];

  const [persons, setPersons] = useState([]);

  const [activities, setActivities] = useState([]);

  const handlePersonUpdate = (updatedPersons) => {
    setPersons(updatedPersons);
    
    const updatedActivities = activities?.map(activity => {
      const person = updatedPersons?.find(p => p?.id === activity?.personId);
      if (person) {
        return {
          ...activity,
          personName: person?.name,
          personColor: person?.color,
          personInitials: person?.initials
        };
      }
      return activity;
    });
    
    setActivities(updatedActivities);
  };

  const handleViewModeChange = (mode) => {
    setViewMode(mode);
  };

  const handlePreviousPeriod = () => {
    const newDate = new Date(currentDate);
    if (viewMode === 'month') {
      newDate?.setMonth(currentDate?.getMonth() - 1);
    } else if (viewMode === 'week') {
      newDate?.setDate(currentDate?.getDate() - 7);
    } else {
      newDate?.setDate(currentDate?.getDate() - 1);
    }
    setCurrentDate(newDate);
  };

  const handleNextPeriod = () => {
    const newDate = new Date(currentDate);
    if (viewMode === 'month') {
      newDate?.setMonth(currentDate?.getMonth() + 1);
    } else if (viewMode === 'week') {
      newDate?.setDate(currentDate?.getDate() + 7);
    } else {
      newDate?.setDate(currentDate?.getDate() + 1);
    }
    setCurrentDate(newDate);
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  const handleActivityClick = (activity) => {
    setSelectedActivity(activity);
    setIsActivityModalOpen(true);
  };

  const handleDateClick = (date) => {
    setSelectedDate(date);
    setSelectedActivity(null);
    setIsActivityModalOpen(true);
  };

  const handleActivitySave = (activityData) => {
    const updatedActivities = [...activities];
    const existingIndex = updatedActivities?.findIndex(a => a?.id === activityData?.id);

    if (existingIndex !== -1) {
      updatedActivities[existingIndex] = activityData;
    } else {
      updatedActivities?.push(activityData);
    }

    setActivities(updatedActivities);
    setIsActivityModalOpen(false);
    setSelectedActivity(null);
  };

  const handleDragStart = (e, activity) => {
    setDraggedActivity(activity);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDrop = (e, newDate) => {
    e?.preventDefault();
    
    if (draggedActivity) {
      const dateStr = newDate?.toISOString()?.split('T')?.[0];
      const timeStr = newDate?.toTimeString()?.slice(0, 5);
      
      const updatedActivities = activities?.map(activity =>
        activity?.id === draggedActivity?.id
          ? { ...activity, date: dateStr, time: timeStr }
          : activity
      );
      
      setActivities(updatedActivities);
      setDraggedActivity(null);
    }
  };

  const handleQuickAction = (action) => {
    if (action?.id === 'add-activity' || action?.id === 'add-event') {
      setSelectedActivity(null);
      setSelectedDate(new Date());
      setIsActivityModalOpen(true);
    }
  };

  const handleSearchChange = (query) => {
    setSearchQuery(query);
  };

  const handleFilterChange = (personIds) => {
    setSelectedPersonFilters(personIds);
  };

  const handleDeleteActivity = (activityId) => {
    setActivities(prev => prev?.filter(a => a?.id !== activityId));
  };

  const handleImportGoogleEvents = (importedEvents) => {
    // Filter out duplicates based on googleEventId
    const existingGoogleIds = new Set(
      activities
        ?.filter(a => a?.googleEventId)
        ?.map(a => a?.googleEventId)
    );

    const newEvents = importedEvents?.filter(
      event => !existingGoogleIds?.has(event?.googleEventId)
    );

    if (newEvents?.length > 0) {
      setActivities(prev => [...prev, ...newEvents]);
      alert(`Successfully imported ${newEvents?.length} events from Google Calendar`);
    } else {
      alert('No new events to import. All events are already in your calendar.');
    }
  };

  const getFilteredActivities = () => {
    let filtered = [...activities];
    
    if (searchQuery) {
      filtered = filtered?.filter(activity => {
        const personNames = activity?.personNames || (activity?.personName ? [activity?.personName] : []);
        const personNamesStr = personNames?.join(' ')?.toLowerCase();
        
        return activity?.title?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
               personNamesStr?.includes(searchQuery?.toLowerCase()) ||
               activity?.location?.toLowerCase()?.includes(searchQuery?.toLowerCase());
      });
    }
    
    if (selectedPersonFilters?.length > 0) {
      filtered = filtered?.filter(activity => {
        const activityPersonIds = activity?.personIds || (activity?.personId ? [activity?.personId] : []);
        return activityPersonIds?.some(id => selectedPersonFilters?.includes(id));
      });
    }
    
    return filtered;
  };

  const getActivitiesForDate = (date) => {
    const dateStr = date?.toISOString()?.split('T')?.[0];
    const directActivities = activities?.filter(activity => activity?.date === dateStr);
    
    // Generate recurring instances for this date
    const recurringActivities = activities
      ?.filter(activity => hasRecurringSchedule(activity))
      ?.flatMap(activity => {
        const instances = generateRecurringInstances(
          activity,
          activity?.recurringConfig,
          activity?.date,
          dateStr
        );
        return instances?.filter(instance => instance?.date === dateStr);
      });

    return [...directActivities, ...recurringActivities];
  };

  const getActivitiesForWeek = (startDate) => {
    const weekActivities = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(startDate);
      date?.setDate(date?.getDate() + i);
      const dateStr = date?.toISOString()?.split('T')?.[0];
      
      const directActivities = activities?.filter(activity => activity?.date === dateStr);
      
      // Generate recurring instances for this date
      const recurringActivities = activities
        ?.filter(activity => hasRecurringSchedule(activity))
        ?.flatMap(activity => {
          const instances = generateRecurringInstances(
            activity,
            activity?.recurringConfig,
            activity?.date,
            dateStr
          );
          return instances?.filter(instance => instance?.date === dateStr);
        });

      weekActivities?.push(...directActivities, ...recurringActivities);
    }
    return weekActivities;
  };

  const getActivitiesForMonth = (year, month) => {
    const monthStart = new Date(year, month, 1)?.toISOString()?.split('T')?.[0];
    const monthEnd = new Date(year, month + 1, 0)?.toISOString()?.split('T')?.[0];
    
    const directActivities = activities?.filter(activity => {
      return activity?.date >= monthStart && activity?.date <= monthEnd;
    });

    // Generate recurring instances for the month
    const recurringActivities = activities
      ?.filter(activity => hasRecurringSchedule(activity))
      ?.flatMap(activity => {
        return generateRecurringInstances(
          activity,
          activity?.recurringConfig,
          monthStart,
          monthEnd
        );
      });

    return [...directActivities, ...recurringActivities];
  };

  const filteredActivities = getFilteredActivities();

  // Generate all recurring instances
  const allActivitiesWithRecurring = useMemo(() => {
    const allActivities = [];
    const startDate = new Date(currentDate);
    startDate?.setDate(1);
    const endDate = new Date(currentDate);
    endDate?.setMonth(endDate?.getMonth() + 1);
    endDate?.setDate(0);

    activities?.forEach(activity => {
      if (activity?.recurringConfig) {
        const instances = generateRecurringInstances(activity, activity?.recurringConfig, startDate, endDate);
        allActivities?.push(...instances);
      } else {
        allActivities?.push(activity);
      }
    });

    return allActivities;
  }, [activities, currentDate]);

  // Check for upcoming reminders
  useEffect(() => {
    const checkReminders = () => {
      const upcomingReminders = getUpcomingReminders(allActivitiesWithRecurring, notificationSettings);
      const activeOnes = upcomingReminders?.filter(r => 
        shouldShowReminder(r) && !dismissedReminders?.includes(r?.id)
      );
      setActiveReminders(activeOnes);
    };

    checkReminders();
    const interval = setInterval(checkReminders, 60000); // Check every minute

    return () => clearInterval(interval);
  }, [allActivitiesWithRecurring, notificationSettings, dismissedReminders]);

  const handleDismissReminder = (reminderId) => {
    setDismissedReminders(prev => [...prev, reminderId]);
  };

  return (
    <>
      <Helmet>
        <title>Family Calendar - Starling Schedule</title>
      </Helmet>
      <div className="min-h-screen bg-background">
        <TabNavigation />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
              <div>
                <h1 className="text-h1 font-heading text-foreground mb-2">
                  Starling Family Calendar
                </h1>
                <p className="text-muted-foreground">
                  Coordinate family schedules and activities in one place
                </p>
              </div>
              <div className="flex items-center gap-3">
                <ExportImportControls 
                  activities={allActivitiesWithRecurring} 
                  persons={persons}
                  currentDate={currentDate}
                />
                <GoogleCalendarSyncButton
                  onImportEvents={handleImportGoogleEvents}
                  persons={persons}
                  preferences={preferences}
                />
              </div>
            </div>
          </div>
          <div className="flex flex-col lg:flex-row gap-6 md:gap-8">
            <div className="flex-1 min-w-0">
              <SearchFilter
                persons={persons}
                onFilterChange={handleFilterChange}
                onSearchChange={handleSearchChange}
              />

              <CalendarHeader
                currentDate={currentDate}
                viewMode={viewMode}
                onViewModeChange={handleViewModeChange}
                onPreviousPeriod={handlePreviousPeriod}
                onNextPeriod={handleNextPeriod}
                onToday={handleToday}
              />

              {viewMode === 'month' && (
                <CalendarGrid
                  currentDate={currentDate}
                  activities={filteredActivities}
                  onActivityClick={handleActivityClick}
                  onDateClick={handleDateClick}
                  onDragStart={handleDragStart}
                  onDrop={handleDrop}
                  onDragOver={(e) => e?.preventDefault()}
                  preferences={preferences}
                  persons={persons}
                />
              )}

              {viewMode === 'week' && (
                <WeekView
                  currentDate={currentDate}
                  activities={filteredActivities}
                  onActivityClick={handleActivityClick}
                  onDateClick={handleDateClick}
                  onDragStart={handleDragStart}
                  onDrop={handleDrop}
                  preferences={preferences}
                  persons={persons}
                />
              )}

              {viewMode === 'day' && (
                <DayView
                  currentDate={currentDate}
                  activities={filteredActivities}
                  onActivityClick={handleActivityClick}
                  onDragStart={handleDragStart}
                  onDrop={handleDrop}
                  preferences={preferences}
                  persons={persons}
                />
              )}
            </div>

            <aside className="lg:w-80 flex-shrink-0">
              <PersonColorLegend
                layout="vertical"
                onPersonUpdate={handlePersonUpdate}
              />
            </aside>
          </div>
        </div>

        <ReminderIndicator 
          reminders={activeReminders}
          onDismiss={handleDismissReminder}
        />

        <ActivityModal
          isOpen={isActivityModalOpen}
          onClose={() => {
            setIsActivityModalOpen(false);
            setSelectedActivity(null);
            setSelectedDate(null);
          }}
          onSave={handleActivitySave}
          activity={selectedActivity}
          persons={persons}
          selectedDate={selectedDate}
        />

        <QuickActionBar
          context="calendar"
          onActionSelect={handleQuickAction}
        />
      </div>
    </>
  );
};

export default FamilyCalendarView;